import { useState, useEffect } from 'react';
import { getDomainByHostname, type CustomDomain } from '@/services/domainService';
import { fetchManifestByHost, type ViewerManifest } from '@/utils/viewerManifest';

interface UseCustomDomainResult {
  siteId: string | null;
  domain: CustomDomain | null;
  manifest: ViewerManifest | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Resolves the active site's identifier and metadata by inspecting the current hostname.
 *
 * The hook attempts multiple strategies—client-side Firestore lookups followed by a service API
 * call—to determine whether the request is served from a custom domain. Suspended domains surface
 * a descriptive error to help consuming components render compliance messaging.
 *
 * @returns Loading state, resolved site identifier, matching domain record, and any lookup error.
 */
export const useCustomDomain = (): UseCustomDomainResult => {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [domain, setDomain] = useState<CustomDomain | null>(null);
  const [manifest, setManifest] = useState<ViewerManifest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const detectCustomDomain = async () => {
      try {
        const hostname = window.location.hostname;
        
        // Skip detection for development and known app domains
        const isDevelopment = hostname === 'localhost' || hostname.includes('127.0.0.1');
        const isAppDomain = hostname.includes('genlp.ai') || hostname.includes('firebase');
        
        if (isDevelopment || isAppDomain) {
          // For dev/app domains, we might get siteId from URL params or other means
          // For now, set as null to indicate no custom domain
          setSiteId(null);
          setDomain(null);
          setIsLoading(false);
          return;
        }

        try {
          const hostManifest = await fetchManifestByHost(hostname);
          if (hostManifest) {
            setManifest(hostManifest);

            if (hostManifest.suspension?.active) {
              setSiteId(hostManifest.siteId ?? null);
              setDomain(null);
              setError(
                hostManifest.suspension.message ||
                  "This site is currently suspended"
              );
              setIsLoading(false);
              return;
            }

            setSiteId(hostManifest.siteId);
            setDomain(null);
            setError(null);
            setIsLoading(false);
            return;
          }
        } catch (manifestError) {
          console.warn(
            '[custom-domain] Host manifest lookup failed, falling back to API',
            manifestError
          );
          setManifest(null);
        }

        const domainInfo = await getDomainByHostname(hostname);
        if (domainInfo && domainInfo.status === 'active') {
          setSiteId(domainInfo.siteId);
          setDomain(domainInfo);
          setManifest(null);
          setError(null);
        } else {
          setError(`Custom domain "${hostname}" not found or not active`);
          setManifest(null);
          setDomain(null);
        }
      } catch (err) {
        console.error('Error detecting custom domain:', err);
        setError('Failed to detect custom domain');
      } finally {
        setIsLoading(false);
      }
    };

    detectCustomDomain();
  }, []);

  return {
    siteId,
    domain,
    manifest,
    isLoading,
    error
  };
};
